import Navigation from "./Navigation";
import { createPortal } from "react-dom";
import { useRef, useEffect, useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { useTheme } from "@/context/ThemeContext";
import { useLanguage } from "@/context/LanguageContext";
import { commonMenuLabels } from "@/i18n";
import { Switch } from "@/components/ui/switch";
import { Moon, Sun } from "lucide-react";

interface MobileMenuProps {
  isOpen: boolean;
  onClose?: () => void;
  menuButtonRef?: React.RefObject<HTMLButtonElement>;
  isDesktopLoggedOut?: boolean;
  onDropdownMouseEnter?: () => void;
  onDropdownMouseLeave?: () => void;
}

const MobileMenu = ({
  isOpen,
  onClose,
  menuButtonRef,
  isDesktopLoggedOut,
  onDropdownMouseEnter,
  onDropdownMouseLeave,
}: MobileMenuProps) => {
  const { user } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { language } = useLanguage();
  const menuLabels = commonMenuLabels(language);
  const isDark = theme === "dark";
  const [dropdownPosition, setDropdownPosition] = useState({
    top: 0,
    right: 0,
  });
  const dropdownContentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen && menuButtonRef?.current) {
      const rect = menuButtonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 4,
        right: window.innerWidth - rect.right,
      });
    }
  }, [isOpen, menuButtonRef]);

  // Close dropdown when clicking outside (mobile only)
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const clickedButton = menuButtonRef?.current?.contains(target);
      const clickedDropdown = dropdownContentRef.current?.contains(target);
      if (!clickedButton && !clickedDropdown) {
        onClose?.();
      }
    };

    document.addEventListener("click", handleClickOutside, true);
    return () =>
      document.removeEventListener("click", handleClickOutside, true);
  }, [isOpen, menuButtonRef, onClose]);

  if (!isOpen) return null;

  const DropdownContent = () => (
    <div
      ref={dropdownContentRef}
      className="fixed w-48 bg-white dark:bg-zinc-950 border border-gray-200 dark:border-white/15 rounded-md shadow-xl z-[100]"
      style={{
        top: dropdownPosition.top,
        right: dropdownPosition.right,
      }}
      onClick={(e) => e.stopPropagation()}
      onMouseEnter={isDesktopLoggedOut ? onDropdownMouseEnter : undefined}
      onMouseLeave={isDesktopLoggedOut ? onDropdownMouseLeave : undefined}
    >
      <Navigation isMobile={true} onClose={onClose} />
      {!user && (
        <>
          <div className="border-t border-gray-200 dark:border-white/10 my-1" />
          <div className="pl-4 pr-1 py-2 flex items-center justify-between">
            <div className="flex items-center">
              {isDark ? (
                <Moon className="h-3 w-3 mr-2" />
              ) : (
                <Sun className="h-3 w-3 mr-2" />
              )}
              <span className="text-base md:text-sm">
                {isDark
                  ? menuLabels.darkModeActive
                  : menuLabels.lightModeActive}
              </span>
            </div>
            <Switch
              checked={isDark}
              onCheckedChange={toggleTheme}
              className="scale-75"
            />
          </div>
        </>
      )}
    </div>
  );

  return createPortal(<DropdownContent />, document.body);
};

export default MobileMenu;
